/**
 * Unified API Client
 *
 * Single source of truth for all API communication following the unified architecture:
 * - All routes go through the configured gateway URL at /v1/{domain}/{resource}
 * - Clean, predictable patterns
 * - Type-safe operations
 * - Centralized auth and error handling
 */

import ky, { type KyInstance, HTTPError } from 'ky'
import { handleUnauthorized } from '@agion/auth'
import { keysToCamel, keysToSnake } from '@/shared/lib/utils'
import { getRequiredConfig } from '@/shared/config/runtime-env'
import { UnifiedApiError, UnifiedApiError as ApiError, adapterFetch, adapterError } from '@/lib/api-error'
import { adapterRetryUnlessRefused, retryAfterAnswer } from '@/shared/api/query-policies'

// ============= Configuration =============

const GATEWAY_URL = getRequiredConfig('VITE_API_URL', 'http://localhost:8080')
const API_TIMEOUT = 30000
const RETRY_ATTEMPTS = 3

// ============= Error Handling =============

// The admin console has one adapter failure (ADR-20260925 rule 1): this
// client throws the same UnifiedApiError as src/lib/api-client.ts, under the
// name its callers already import. `serverRetryable` is the body's own
// `retryable` verdict; `retryable` is the transport's (no response, 408, 429
// or a 5xx).
export { UnifiedApiError as ApiError }

// ============= BFF Pattern Authentication =============
//
// BFF (Backend-For-Frontend) Pattern:
// - JWT tokens are stored server-side in Redis (not in browser)
// - Browser receives opaque session ID in HttpOnly cookie (agion_sso_session)
// - APISIX forward-auth plugin resolves session → JWT for API requests
// - Client only needs to send cookies via credentials: 'include'
//
// NO localStorage, NO cookie reading, NO Authorization header needed
// Authentication is handled entirely by the agion_sso_session HttpOnly cookie

// These functions are kept for backward compatibility but are no-ops
export function setAuthToken(_token: string | null) {
  // BFF Pattern: No client-side token storage
  console.warn('[Auth] setAuthToken called but BFF pattern is active - token not stored')
}

export function getAuthToken(): string | null {
  // BFF Pattern: No client-side token storage
  // Authentication is handled via HttpOnly cookies
  return null
}

// ============= HTTP Client =============

export function applyBFFRequestHeaders(request: Request): void {
  // Keep browser GETs CORS-simple. APISIX forward-auth derives the authoritative
  // organization from the HttpOnly session and injects it upstream; accepting a
  // browser-supplied tenant header here only adds a preflight failure mode.
  request.headers.set('Accept', 'application/json')
}

const httpClient: KyInstance = ky.create({
  prefix: `${GATEWAY_URL}/v1`,
  // Preserve Ky 1's successful empty-body result for command endpoints.
  parseJson: text => text === '' ? '' : JSON.parse(text),
  timeout: API_TIMEOUT,
  credentials: 'include', // BFF Pattern: Send agion_sso_session cookie with every request
  retry: {
    limit: RETRY_ATTEMPTS,
    methods: ['get'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
    // afterResponse throws ApiError rather than ky's HTTPError. Without
    // this guard ky retries terminal bodies as ordinary transport errors.
    // A 4xx other than 408 and 429 is not asked again either
    // (agion/Agion#2727, agion/Agion#2777).
    shouldRetry: (state) =>
      state.error instanceof ApiError && state.error.serverRetryable === false ? false : adapterRetryUnlessRefused(state),
  },
  fetch: adapterFetch,
  hooks: {
    beforeRequest: [
      ({ request }) => {
        // BFF Pattern: No Authorization header needed
        // Authentication is handled by APISIX forward-auth plugin which:
        // 1. Reads the agion_sso_session cookie from the request
        // 2. Calls auth-service /session/resolve to get the JWT
        // 3. Injects the Authorization header into upstream requests
        //
        // The client just needs credentials: 'include' to send the cookie

        // ky sets Content-Type for JSON bodies. Do not add it to bodyless GETs:
        // together with a browser-provided tenant header that forced every read
        // through a CORS preflight before the API request could reach APISIX.
        applyBFFRequestHeaders(request)
      }
    ],
    afterResponse: [
      async (state) => {
        const { response } = state
        if (!response.ok) {
          let errorData: unknown
          let errorCode: string | undefined
          let errorReason: string | undefined

          try {
            const json = await response.json() as Record<string, unknown>
            errorData = json
            errorCode = (json['code'] as string | undefined) || (json['error_code'] as string | undefined)
            // Prefer the server's reason over HTTP statusText (empty on
            // HTTP/2), so err.message carries actionable text like
            // "Unsupported pack format version 2 (supported: 1)" instead
            // of "API Error 400:".
            errorReason = [json['message'], json['error'], json['details']].find(
              (v): v is string => typeof v === 'string' && v.trim() !== ''
            )
          } catch {
            errorData = response.statusText
          }

          // Soft-401 (must run BEFORE throwing) — beforeError only fires
          // for ky's own HTTPError, not for our thrown ApiError below.
          if (response.status === 401) {
            void handleUnauthorized()
          }

          const comeBack = retryAfterAnswer(state, errorData)
          if (comeBack) return comeBack
          throw new ApiError(
            response.status,
            errorReason || response.statusText || `HTTP ${response.status}`,
            errorData,
            errorCode
          )
        }
        return undefined
      }
    ],
    beforeError: [
      ({ error }) => {
        // 401 handling lives in afterResponse — see comment there.
        if (error instanceof HTTPError) {
          if (error.response.status === 403) {
            // Redirect to unauthorized page on forbidden access
            if (window.location.pathname !== '/unauthorized') {
              window.location.href = '/unauthorized'
            }
          }
        }
        return adapterError(error)
      }
    ]
  }
})

// ============= Base API Client =============
// Note: This client handles automatic case conversion:
// - Request data: camelCase → snake_case (sent to backend)
// - Response data: snake_case → camelCase (returned to frontend)

/** Per-call override for requests. Use for endpoints that hit slow /
 *  possibly-unreachable upstreams (e.g. VPN-backed Edge hosts) where the
 *  default 30s × 3-retry behavior causes perceived "loads forever". */
export interface RequestOptions {
  /** Ky timeout in ms for this single request. Overrides the 30s default. */
  timeout?: number
  /** Ky retry count. Pass 0 to disable retries for this call. */
  retry?: number
  /** Abort signal for caller-driven cancellation. */
  signal?: AbortSignal
}

export type GetRequestOptions = RequestOptions

export const apiClient = {
  // Generic HTTP methods with automatic case conversion
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean>,
    options?: GetRequestOptions,
  ): Promise<T> {
    const response = await httpClient
      .get(path, {
        searchParams: params as Record<string, string>,
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
        ...(options?.retry !== undefined ? { retry: options.retry } : {}),
        ...(options?.signal ? { signal: options.signal } : {}),
      })
      .json()
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(
    path: string,
    data?: D,
    options?: RequestOptions,
  ): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient
      .post(path, {
        json: snakeCaseData,
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
        ...(options?.retry !== undefined ? { retry: options.retry } : {}),
        ...(options?.signal ? { signal: options.signal } : {}),
      })
      .json()
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient.put(path, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  },

  async patch<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient.patch(path, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  },

  // PATCH without snake/camel conversion in either direction, complementing
  // the existing getRaw/postRaw for verbatim wire keys (extraction label
  // annotations). Ported from reboot/main client.
  async patchRaw<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    return httpClient
      .patch(path, {
        json: data,
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
        ...(options?.retry !== undefined ? { retry: options.retry } : {}),
        ...(options?.signal ? { signal: options.signal } : {}),
      })
      .json<T>()
  },

  async delete<T = void, TBody = unknown>(path: string, body?: TBody): Promise<T> {
    const snakeCaseBody = body ? keysToSnake(body) : undefined
    const response = await httpClient.delete(
      path,
      snakeCaseBody ? { json: snakeCaseBody } : undefined,
    )
    // Handle 204 No Content (empty body) — don't try to parse JSON
    if (response.status === 204) {
      return undefined as T
    }
    const json = await response.json()
    return keysToCamel<T>(json)
  },

  // File upload - response converted to camelCase
  async upload<T>(path: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData()
    formData.append(fieldName, file)

    const response = await httpClient.post(path, {
      body: formData,
      // Let browser set Content-Type with boundary for multipart/form-data
      headers: {
        // Remove Content-Type so browser sets it correctly
      }
    }).json()
    return keysToCamel<T>(response)
  },

  // Stream response - no case conversion needed for binary streams
  async getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
    return httpClient.get(path, { signal, cache: 'no-store', retry: 0 }).blob()
  },

  async stream(path: string): Promise<ReadableStream<Uint8Array> | null> {
    const response = await httpClient.get(path)
    return response.body
  },

  // Raw JSON methods — no camelCase/snake_case conversion.
  // Use for endpoints with user-defined field names (e.g., capsule_data columns).
  async getRaw<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
    return httpClient.get(path, { searchParams: params as Record<string, string> }).json<T>()
  },

  // PUT without conversion, for bodies whose keys are data rather than field
  // names (a governed source pin is keyed by operation id and stream name).
  async putRaw<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    return httpClient.put(path, {
      json: data,
      ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      ...(options?.retry !== undefined ? { retry: options.retry } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    }).json<T>()
  },

  async postRaw<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    return httpClient.post(path, {
      json: data,
      ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      ...(options?.retry !== undefined ? { retry: options.retry } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    }).json<T>()
  },
}

// ============= Response Types =============

export interface ApiResponse<T> {
  data?: T
  message?: string
  success?: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  offset: number
  limit: number
}

export interface ListResponse<T> {
  items?: T[]
  resources?: T[]
  [key: string]: unknown
}
